import { motion } from "framer-motion";
import { BedDouble, CarFront } from "lucide-react";

export const Sleeping = ({ sleeping, property, photo }) => (
  <section id="sleeping" className="scroll-mt-24 py-16 sm:py-24" aria-label="Sleeping arrangements" data-testid="sleeping-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-5 gap-8 lg:gap-14 items-start">
      <motion.div
        initial={{ opacity: 0, x: -28 }} whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="lg:col-span-2"
      >
        <div className="relative rounded-3xl overflow-hidden shadow-xl">
          <img src={photo} alt="A restful Pebble Farms bedroom" className="w-full aspect-[4/5] object-cover" loading="lazy" />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-charcoal/80 to-transparent p-6">
            <div className="flex gap-6 text-cream">
              <span><strong className="font-serif text-2xl">{property.bedrooms}</strong> <span className="text-cream/80 text-sm">bedrooms</span></span>
              <span><strong className="font-serif text-2xl">6</strong> <span className="text-cream/80 text-sm">beds</span></span>
              <span><strong className="font-serif text-2xl">{property.bathrooms}</strong> <span className="text-cream/80 text-sm">baths</span></span>
            </div>
          </div>
        </div>
      </motion.div>
      <div className="lg:col-span-3">
        <p className="text-xs uppercase tracking-[0.24em] text-copper font-semibold mb-2">Sleeping Arrangements</p>
        <h2 className="font-serif text-3xl sm:text-5xl font-semibold text-charcoal mb-4">Room for <em className="text-copperdeep">everyone</em></h2>
        <p className="text-soft text-lg mb-8">{sleeping.intro}</p>
        <div className="space-y-3">
          {sleeping.rooms.map((room, i) => (
            <motion.div
              key={room.name}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }} transition={{ delay: i * 0.06, duration: 0.45 }}
              className="bg-white border border-charcoal/10 rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-3"
              data-testid={`room-${room.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-3.5">
                <BedDouble size={20} className="text-copper shrink-0" aria-hidden />
                <div>
                  <p className="font-semibold text-charcoal">{room.name} · <span className="text-copperdeep">{room.detail}</span></p>
                  {room.note && <p className="text-sm text-soft">{room.note}</p>}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {sleeping.chips.map((chip) => (
                  <span key={chip} className="text-xs font-semibold bg-sand text-charcoal/80 rounded-full px-3 py-1.5">{chip}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const Location = ({ location, photo }) => (
  <section id="location" className="scroll-mt-24 bg-charcoal text-cream py-16 sm:py-24" aria-label="Location and neighborhood" data-testid="location-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
      <div className="mb-12">
        <p className="text-xs uppercase tracking-[0.24em] text-[#E8A97E] font-semibold mb-2">Location & Neighborhood</p>
        <h2 className="font-serif text-3xl sm:text-5xl font-semibold">In the heart of <em className="text-[#EBC9A8]">Canyon Lake</em></h2>
        <p className="text-cream/75 text-lg mt-4 max-w-3xl">{location.intro}</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
        <div className="space-y-8">
          {location.itineraries.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }} transition={{ delay: i * 0.1, duration: 0.55 }}
              className="flex gap-5"
              data-testid={`itinerary-${i + 1}`}
            >
              <span className="font-serif italic text-4xl text-[#E8A97E] shrink-0" aria-hidden>{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="font-serif text-xl sm:text-2xl font-semibold mb-2">{it.title}</h3>
                <p className="text-cream/75 leading-relaxed">{it.body}</p>
              </div>
            </motion.div>
          ))}
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="rounded-2xl overflow-hidden"
          >
            <img src={photo} alt="The Pebble Farms farmhouse at dusk" className="w-full aspect-[16/9] object-cover" loading="lazy" />
          </motion.div>
        </div>
        <div>
          <h3 className="flex items-center gap-2.5 text-xs uppercase tracking-[0.24em] text-[#E8A97E] font-semibold mb-5">
            <CarFront size={15} aria-hidden /> Drive Times
          </h3>
          <ul className="divide-y divide-cream/10">
            {location.drive_times.map((d, i) => (
              <motion.li
                key={d.place}
                initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-20px" }} transition={{ delay: i * 0.04, duration: 0.4 }}
                className="flex items-baseline justify-between gap-4 py-3.5"
                data-testid={`drive-${d.place.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 26)}`}
              >
                <span className="text-cream/90">{d.place}</span>
                <span className="font-serif italic text-[#E8A97E] shrink-0">{d.time}</span>
              </motion.li>
            ))}
          </ul>
          <p className="text-cream/50 text-sm mt-4 italic">{location.note}</p>
        </div>
      </div>
    </div>
  </section>
);
